// Which slot does an exercise belong to?
//
// A slot is a movement group (Chin-up, Squat, Rear delt, ...) and each slot document
// lists the exercises that belong to it. The same exercise can appear in more than one
// slot, so "its slot" means the slot where it sits at the lowest position in that list.
//
// This rule used to be written out twice — once in `exercise-switching.ts` and once
// inside `ExerciseDbService.slotForGlobalExerciseIdAsync` — and the two copies did NOT
// agree about the exercise that is in no slot at all. One returned "I do not know", the
// other returned the first slot in the database, which is the abs slot, so anything
// asking about an unslotted exercise was told it trains abs (#4978). One copy, one
// answer.
//
// The module deliberately imports nothing: the parameter type is structural, so a unit
// test can hand it plain objects and neither Angular nor the exercise database has to
// be loaded.

/** The part of an exercise-slot document this lookup reads. */
export interface SlotWithExercises {
  _id: string;
  exercises?: { exerciseId: string }[];
}

/**
 * Return the slot the exercise belongs to — the one where it appears at the lowest
 * index — or `undefined` when no slot lists it.
 *
 * `undefined` means "no slot lists this exercise", and every caller must treat it as
 * that rather than substituting some other slot. A user's own exercise (`+UDE-`) with
 * no group legitimately has no slot; a curated one having none is a gap in the catalog.
 */
export function pickSlotForExercise<T extends SlotWithExercises>(
  slots: readonly T[],
  exerciseId: string,
): T | undefined {
  let bestSlot: T | undefined = undefined;
  let bestIndex = Number.MAX_SAFE_INTEGER;
  for (const slot of slots) {
    const index = (slot.exercises ?? []).findIndex(e => e.exerciseId === exerciseId);
    if (index >= 0 && index < bestIndex) {
      bestIndex = index;
      bestSlot = slot;
    }
  }
  return bestSlot;
}
